/**
 * Centralized Utilization Label System
 * 
 * This module provides a standardized way to get utilization labels
 * across the entire application. It uses the user's custom labels
 * from settings, ensuring consistency everywhere.
 */

import { UtilisationTier } from '@/types';

/**
 * Get only enabled utilization tiers
 * 
 * @param tiers - The utilization tiers from settings
 * @returns Array of enabled tiers only
 */
export function getEnabledTiers(tiers: UtilisationTier[]): UtilisationTier[] {
  return tiers.filter((tier) => tier.enabled);
}

/**
 * Get the utilization label based on percentage and user settings
 * Only considers enabled tiers
 * 
 * @param utilization - The utilization percentage (0-999+)
 * @param tiers - The utilization tiers from settings
 * @returns The label for the given utilization percentage
 */
export function getUtilizationLabel(
  utilization: number,
  tiers: UtilisationTier[]
): string {
  const enabledTiers = getEnabledTiers(tiers);
  
  const tier = enabledTiers.find(
    (t) => utilization >= t.minPercentage && utilization <= t.maxPercentage
  );

  if (tier) {
    return tier.customLabel || tier.label;
  }

  const lastTier = enabledTiers[enabledTiers.length - 1];
  return lastTier?.customLabel || lastTier?.label || 'Unknown';
}

/**
 * Get the utilization color based on percentage and user settings
 * Only considers enabled tiers
 * 
 * @param utilization - The utilization percentage (0-999+)
 * @param tiers - The utilization tiers from settings
 * @returns The color for the given utilization percentage
 */
export function getUtilizationColor(
  utilization: number,
  tiers: UtilisationTier[]
): string {
  const enabledTiers = getEnabledTiers(tiers);
  
  const tier = enabledTiers.find(
    (t) => utilization >= t.minPercentage && utilization <= t.maxPercentage
  );

  return tier?.color || '#6b7280';
}

export function getUtilizationColorCapped(
  utilization: number,
  tiers: UtilisationTier[]
): string {
  const cappedUtilization = Math.min(utilization, 100);
  const enabledTiers = getEnabledTiers(tiers);

  const tier = enabledTiers.find(
    (t) => cappedUtilization >= t.minPercentage && cappedUtilization <= t.maxPercentage
  );

  return tier?.color || '#6b7280';
}

/**
 * Get the utilization tier object based on percentage
 * Only considers enabled tiers
 * 
 * @param utilization - The utilization percentage (0-999+)
 * @param tiers - The utilization tiers from settings
 * @returns The tier object for the given utilization percentage
 */
export function getUtilizationTier(
  utilization: number,
  tiers: UtilisationTier[]
): UtilisationTier | undefined {
  const enabledTiers = getEnabledTiers(tiers);
  
  return enabledTiers.find(
    (t) => utilization >= t.minPercentage && utilization <= t.maxPercentage
  );
}

/**
 * Get all utilization tiers for display (e.g., in legends)
 * Returns only enabled tiers with their display labels (custom or default)
 * 
 * @param tiers - The utilization tiers from settings
 * @returns Array of enabled tiers with display information
 */
export function getUtilizationLegend(
  tiers: UtilisationTier[]
): Array<{ label: string; color: string; range: string }> {
  const enabledTiers = getEnabledTiers(tiers);
  
  return enabledTiers.map((tier) => ({
    label: tier.customLabel || tier.label,
    color: tier.color,
    range: tier.minPercentage === tier.maxPercentage
      ? `${tier.minPercentage}%`
      : `${tier.minPercentage}-${tier.maxPercentage}%`,
  }));
}

/**
 * Get utilization category for accessibility and styling
 * Maps to semantic categories regardless of custom labels
 * 
 * @param utilization - The utilization percentage (0-999+)
 * @returns Semantic category
 */
export function getUtilizationCategory(
  utilization: number
): 'none' | 'low' | 'optimal' | 'high' | 'over' {
  if (utilization === 0) return 'none';
  if (utilization < 50) return 'low';
  if (utilization >= 50 && utilization <= 85) return 'optimal';
  if (utilization > 85 && utilization <= 100) return 'high';
  return 'over';
}

/**
 * Default utilization tiers (fallback if settings not available)
 */
// Default tiers match mock-data.ts defaultSettings.utilisationTiers.
// Overloaded intentionally starts at 101% — a resource booked at exactly 100%
// of their contracted hours is performing at capacity and should NOT be flagged.
// Only bookings that EXCEED contracted hours (>100%) should appear as overloaded.
export const DEFAULT_UTILIZATION_TIERS: UtilisationTier[] = [
  { id: 'tier-1', label: 'Available',     color: '#bfdbfe', minPercentage: 0,   maxPercentage: 69,  enabled: true },
  { id: 'tier-2', label: 'On Track',      color: '#bbf7d0', minPercentage: 70,  maxPercentage: 84,  enabled: true },
  { id: 'tier-3', label: 'Near Capacity', color: '#fde68a', minPercentage: 85,  maxPercentage: 100, enabled: true },
  { id: 'tier-4', label: 'Overloaded',    color: '#fecaca', minPercentage: 101, maxPercentage: 999, enabled: true },
];
